import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ErrorApi } from '../lib/api'
import type { EstadoCatalogo, EstadoFicha } from '../lib/tipos'

/**
 * A qué estado lleva cada acción. El servidor tiene la misma tabla y es quien
 * manda; esto solo sirve para poner la etiqueta y saber qué subestados ofrecer.
 */
const DESTINO: Record<string, EstadoFicha> = {
  pendiente: 'PENDIENTE',
  firma: 'PDT_FIRMA',
  activacion: 'PDT_ACTIVACION',
  activar: 'ACTIVADO',
  revision: 'REVISION',
  rechazar: 'RECHAZADO',
  scoring: 'SCORING',
  baja: 'BAJA',
}

/** La fecha es la que da la compañía, no la de hoy. */
const PIDEN_FECHA = ['activar', 'baja']

const CAMPO =
  'rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs outline-none' +
  ' focus:border-marca-500 dark:border-slate-700 dark:bg-slate-950'

/**
 * Cambiar el estado sin entrar en el contrato.
 *
 * «Que se pueda cambiar el estado desde un desplegable al lado del número de
 * pedido, sin tener que entrar en el documento», del 15 de octubre. Y los
 * subestados son condicionales: primero se elige el estado y solo entonces
 * aparecen los suyos, que era la otra petición.
 */
export function CambiarEstado({
  uuid,
  acciones,
  catalogo,
  alFallar,
}: {
  uuid: string
  acciones: string[]
  catalogo: EstadoCatalogo[]
  alFallar: (mensaje: string) => void
}) {
  const clienteQuery = useQueryClient()
  const [accion, setAccion] = useState('')
  const [subestado, setSubestado] = useState('')
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10))

  const mover = useMutation({
    mutationFn: (cual: string) =>
      api(`/api/contratos/${uuid}/${cual}`, {
        metodo: 'POST',
        cuerpo: { fecha, subestado },
      }),
    onSuccess: () => {
      setAccion('')
      setSubestado('')
      void clienteQuery.invalidateQueries({ queryKey: ['fichas'] })
      void clienteQuery.invalidateQueries({ queryKey: ['bajas'] })
      void clienteQuery.invalidateQueries({ queryKey: ['mis-puntos'] })
    },
    onError: (e) => alFallar(e instanceof ErrorApi ? e.message : 'No se ha podido cambiar el estado'),
  })

  if (acciones.length === 0) return null

  // El scoring no cambia de estado: se relanza con otra compañía, y eso abre una
  // llamada nueva con los datos ya tomados.
  if (acciones.length === 1 && acciones[0] === 'recuperar') {
    return (
      <button
        onClick={() => mover.mutate('recuperar')}
        disabled={mover.isPending}
        className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-medium transition
                   hover:border-marca-500 hover:text-marca-700 disabled:opacity-40 dark:border-slate-700"
      >
        Relanzar con otra compañía
      </button>
    )
  }

  const destino = accion === '' ? null : DESTINO[accion]
  // Los subestados que pone el CRM solo (las clases de baja) no se ofrecen.
  const subestados = (catalogo.find((item) => item.codigo === destino)?.subestados ?? []).filter(
    (sub) => sub.automatico === 0,
  )

  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <select
        value={accion}
        onChange={(e) => {
          setAccion(e.target.value)
          setSubestado('')
        }}
        aria-label="Cambiar de estado"
        className={CAMPO}
      >
        <option value="">Cambiar a…</option>
        {acciones
          .filter((cual) => DESTINO[cual] !== undefined)
          .map((cual) => (
            <option key={cual} value={cual}>
              {catalogo.find((item) => item.codigo === DESTINO[cual])?.etiqueta ?? cual}
            </option>
          ))}
      </select>

      {subestados.length > 0 && (
        <select
          value={subestado}
          onChange={(e) => setSubestado(e.target.value)}
          aria-label="Subestado"
          className={CAMPO}
        >
          <option value="">— sin subestado —</option>
          {subestados.map((sub) => (
            <option key={sub.id} value={sub.codigo}>
              {sub.etiqueta}
              {sub.compania ? ` (${sub.compania})` : ''}
            </option>
          ))}
        </select>
      )}

      {accion !== '' && PIDEN_FECHA.includes(accion) && (
        <input
          type="date"
          value={fecha}
          onChange={(e) => setFecha(e.target.value)}
          title="La fecha real que da la compañía, no la de hoy"
          className={CAMPO}
        />
      )}

      {accion !== '' && (
        <button
          onClick={() => mover.mutate(accion)}
          disabled={mover.isPending}
          className="rounded-lg bg-marca-700 px-3 py-1 text-xs font-bold text-white transition
                     hover:bg-marca-800 disabled:opacity-50"
        >
          {mover.isPending ? 'Guardando…' : 'Guardar'}
        </button>
      )}
    </span>
  )
}
